import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { ModifierGroupForm } from "./components/ModifierGroupForm";
import {
  useGetModifierGroupQuery,
  useCreateModifierGroupMutation,
  useUpdateModifierGroupMutation,
  useDeleteModifierGroupMutation,
} from "../query/modifierGroupsQuery";
import type { RestCreateModifierGroup } from "@KassaroAB/shared/types/modifierGroup";

const FORM_ID = "catalog-modifier-group-form";

export const ModifierGroupFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("modifierGroups");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const modifierGroupId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: group,
    isLoading,
    error: loadError,
  } = useGetModifierGroupQuery(scope, modifierGroupId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateModifierGroupMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateModifierGroupMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteModifierGroupMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!modifierGroupId && !isLoading && !group;

  const handleSubmit = (data: RestCreateModifierGroup) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!modifierGroupId) return;
    updateMutation(
      { modifierGroupId, group: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("modifierGroupsView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!modifierGroupId) return;
    deleteMutation(modifierGroupId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("modifierGroupsView.panelCreate")
    : (group?.name ?? t("modifierGroupsView.title"));
  const breadcrumbCurrent = isCreating
    ? t("modifierGroupsView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (group?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!group && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("modifierGroupsView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("modifierGroupsView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("modifierGroupsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !group ? (
          <LoadingContainer message={t("modifierGroupsView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <ModifierGroupForm
              key={group?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              group={isCreating ? undefined : group}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("modifierGroupForm.deleteModifierGroup")}
        message={t("modifierGroupsView.deleteConfirm", {
          name: group?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
